import { Download } from "lucide-react";
import {
  type LifecycleStage,
  StatusBadge,
} from "@/components/status/StatusBadge";
import { Button } from "@/components/ui/button";
import type {
  AnalysisResponse,
  PolizzaExtractResponse,
} from "@/lib/api/extract";
import {
  type DocumentKind,
  getRunFailurePresentation,
  toKindLabel,
  type UnifiedRunStatus,
} from "./status-domain";

type StatusResultSectionProps = {
  analysis: AnalysisResponse | null;
  polizzaAnalysis: PolizzaExtractResponse | null;
  runStatus: UnifiedRunStatus | null;
  effectiveRunId: string | null;
  isResultTransitioning: boolean;
  isFetchingCompletedResult: boolean;
  completedResultError: string | null;
  canDownloadCurrentPdf: boolean;
  isDownloadingCurrentPdf: boolean;
  pdfErrorMessage: string | null;
  analysisDetailsJson: string;
  onDetailsOpenChange: (isOpen: boolean) => void;
  onRetryCompletedResult: () => Promise<void>;
  onDownloadCurrentPdf: (runId: number, kind: DocumentKind) => Promise<void>;
  onGoToUpload: () => void;
};

function toLifecycleStage(
  status: UnifiedRunStatus["status"] | null,
): LifecycleStage {
  if (status === "queued") return "queued";
  if (status === "processing") return "processing";
  if (status === "completed") return "completed";
  if (status === "failed") return "failed";
  return "uploaded";
}

export function StatusResultSection({
  analysis,
  polizzaAnalysis,
  runStatus,
  effectiveRunId,
  isResultTransitioning,
  isFetchingCompletedResult,
  completedResultError,
  canDownloadCurrentPdf,
  isDownloadingCurrentPdf,
  pdfErrorMessage,
  analysisDetailsJson,
  onDetailsOpenChange,
  onRetryCompletedResult,
  onDownloadCurrentPdf,
  onGoToUpload,
}: StatusResultSectionProps) {
  const failurePresentation = getRunFailurePresentation(runStatus);
  const statusStage = toLifecycleStage(runStatus?.status ?? null);
  const showFailurePanel =
    runStatus?.status === "failed" && failurePresentation;

  return (
    <section
      className="status-panel__card status-panel__result"
      aria-label="Risultato estrazione"
    >
      <div className="status-panel__result-header">
        <div className="status-panel__result-heading">
          <h3 className="status-panel__section-title">
            {effectiveRunId
              ? `Risultato analisi ${effectiveRunId}`
              : "Risultato"}
          </h3>
          {runStatus ? (
            <p className="status-panel__result-context">
              {runStatus.inputFilename ?? "n.d."} ·{" "}
              {toKindLabel(runStatus.kind)}
            </p>
          ) : null}
        </div>
        {runStatus ? <StatusBadge stage={statusStage} compact /> : null}
      </div>

      {isResultTransitioning ? (
        <div className="status-panel__result-skeleton" aria-hidden="true">
          <span className="status-panel__skeleton-line status-panel__skeleton-line--lg" />
          <span className="status-panel__skeleton-line status-panel__skeleton-line--md" />
          <span className="status-panel__skeleton-line status-panel__skeleton-line--sm" />
        </div>
      ) : showFailurePanel ? (
        <div
          className="status-panel__error-callout"
          role="alert"
          aria-live="polite"
        >
          <p className="status-panel__error-eyebrow">
            {failurePresentation.title}
          </p>
          <p className="status-panel__error-message">
            {failurePresentation.message}
          </p>
          {failurePresentation.nextStep ? (
            <p className="status-panel__error-guidance">
              Prossimo passo: {failurePresentation.nextStep}
            </p>
          ) : null}
          <div className="status-panel__result-actions">
            <Button type="button" variant="outline" onClick={onGoToUpload}>
              Vai a Carica
            </Button>
          </div>
        </div>
      ) : runStatus?.kind === "fatture_ddt" && analysis ? (
        <>
          <p className="text-sm text-muted-foreground">
            Pagine: {analysis.pages.length} · Mismatch:{" "}
            {analysis.mismatches.length} · Failure pagina:{" "}
            {analysis.page_failures.length}
          </p>
          <div className="status-panel__result-actions">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (!runStatus) return;
                void onDownloadCurrentPdf(runStatus.id, runStatus.kind);
              }}
              disabled={
                !runStatus ||
                runStatus.status !== "completed" ||
                isDownloadingCurrentPdf ||
                !canDownloadCurrentPdf
              }
            >
              <Download size={15} aria-hidden="true" />
              {isDownloadingCurrentPdf ? "Scarico…" : "Scarica report PDF"}
            </Button>
            {!canDownloadCurrentPdf && runStatus?.status === "completed" ? (
              <span className="status-panel__helper">
                PDF non disponibile per questa analisi.
              </span>
            ) : null}
          </div>
        </>
      ) : runStatus?.kind === "polizza_carico" && polizzaAnalysis ? (
        <div className="status-panel__polizza-summary">
          <p className="text-sm text-muted-foreground">
            BL: {polizzaAnalysis.total_bls} · Container:{" "}
            {polizzaAnalysis.total_containers} · Failure pagina:{" "}
            {polizzaAnalysis.page_failures.length}
          </p>
          <div className="status-panel__polizza-groups">
            {polizzaAnalysis.bl_groups.slice(0, 8).map((group) => (
              <div key={group.bl} className="status-panel__polizza-group">
                <span className="status-panel__polizza-bl">BL {group.bl}</span>
                <span className="status-panel__polizza-count">
                  {group.container_count ?? group.containers?.length ?? 0}{" "}
                  container
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : runStatus?.status === "failed" ? (
        <div className="status-panel__result-recovery">
          <p className="text-sm text-muted-foreground">
            Nessun risultato disponibile per questa analisi interrotta.
          </p>
        </div>
      ) : runStatus?.status === "completed" ? (
        <div className="status-panel__result-recovery">
          {isFetchingCompletedResult ? (
            <p className="text-sm text-muted-foreground">
              Analisi completata, recupero risultato in corso…
            </p>
          ) : completedResultError ? (
            <>
              <p className="upload-form__error" role="alert">
                {completedResultError}
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void onRetryCompletedResult()}
              >
                Riprova recupero risultato
              </Button>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Risultato non disponibile al momento. Riprova tra qualche secondo.
            </p>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Il risultato comparira qui quando l'analisi sara completata.
        </p>
      )}

      {pdfErrorMessage ? (
        <p className="upload-form__error" role="alert">
          Errore: {pdfErrorMessage}
        </p>
      ) : null}

      {(analysis || polizzaAnalysis) && runStatus ? (
        <details
          className="status-panel__advanced"
          onToggle={(event) => onDetailsOpenChange(event.currentTarget.open)}
        >
          <summary className="status-panel__advanced-summary">
            Dettagli tecnici
          </summary>
          <pre className="status-panel__payload">{analysisDetailsJson}</pre>
        </details>
      ) : null}
    </section>
  );
}
